import dynamic from "next/dynamic";
import React, { useState, Fragment, useEffect } from "react";
import { createPortal } from "react-dom";
import Modal from "@/components/ui/Modal";
import { Icon } from "@iconify/react/dist/iconify.js";
const Button = dynamic(() => import("@/components/ui/Button"));
import {
  uploadReportScreenshots,
  addUserReport,
  updateUserReport,
  getGames,
} from "@/utils/supabaseFunctions";
import ShortUniqueId from "short-unique-id";
import MessageModal from "@/components/ui/Message-Modal";

function ReportModal({ isOpen, onClose, user, report }) {
  const [uid, setUid] = useState("");
  const [walletAddress, setWalletAddress] = useState("");
  const [currentDateTime, setCurrentDateTime] = useState("");
  const [status, setStatus] = useState("OPEN");
  const [issueArea, setIssueArea] = useState("LOYALTY");
  const [gameName, setGameName] = useState("");
  const [severity, setSeverity] = useState("");
  const [dateOfIssue, setDateOfIssue] = useState("");
  const [description, setDescription] = useState("");
  const [round, setRound] = useState("");
  const [buyFrom, setBuyFrom] = useState("");
  const [purchaseLocation, setPurchaseLocation] = useState("");

  const [subCategory, setSubCategory] = useState("");
  const [claimingFrom, setClaimingFrom] = useState("");
  const [walletType, setWalletType] = useState("");
  const [telegram, setTelegram] = useState("");
  const [email, setEmail] = useState("");
  const [discord, setDiscord] = useState("");

  const [screenshots, setScreenshots] = useState([]);
  const [showFormErrors, setShowFormErrors] = useState(false);
  const reportId = new ShortUniqueId({ length: 12 });

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [activeError, setActiveError] = useState(null);
  const [activeErrorTitle, setActiveErrorTitle] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [games, setGames] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(true);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [activePreviewIndex, setActivePreviewIndex] = useState(null);
  const isAirdropVerification = issueArea === "AIRDROP VERIFICATION";

  useEffect(() => {
    getGamesData();

    if (user) {
      setUid(user.user_id || user.uid);
      setWalletAddress(user.wallet);
    }

    if (report) {
      setIssueArea(report.issue_area);
      setSeverity(report.severity);
      setDateOfIssue(report.date_time_issue);
      setDescription(report.description);
      setScreenshots(report.screenshots);
      setStatus(report.status);
      setCurrentDateTime(report.datetime_current);
      setGameName(report.game_name);
      setSubCategory(report.sub_category || "");
      setClaimingFrom(report.claiming_from || "");
      setWalletType(report.wallet_type || "");
      setTelegram(report.telegram || "");
      setEmail(report.email || "");
      setDiscord(report.discord || "");
    } else {
      setCurrentDateTime(
        new Date().toLocaleDateString("en-US", {
          timeZone: "UTC",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }),
      );
    }

    return () => {
      //console.log("ReportModal unmounted");
    };
  }, []);

  // Build preview URLs for screenshots (string URLs and File objects)
  useEffect(() => {
    try {
      const urls = (screenshots || []).map((item) =>
        typeof item === "string" ? item : URL.createObjectURL(item),
      );
      setPreviewUrls(urls);

      return () => {
        urls.forEach((url, index) => {
          if (screenshots && typeof screenshots[index] !== "string") {
            URL.revokeObjectURL(url);
          }
        });
      };
    } catch (_) {
      // ignore
    }
  }, [screenshots]);

  // Keyboard navigation for preview overlay
  useEffect(() => {
    const handleKey = (e) => {
      if (activePreviewIndex === null) return;
      if (e.key === "Escape") {
        setActivePreviewIndex(null);
      } else if (e.key === "ArrowLeft") {
        setActivePreviewIndex((prev) => Math.max(0, (prev ?? 0) - 1));
      } else if (e.key === "ArrowRight") {
        setActivePreviewIndex((prev) =>
          Math.min(previewUrls.length - 1, (prev ?? 0) + 1),
        );
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [activePreviewIndex, previewUrls.length]);

  const getGamesData = async () => {
    try {
      console.log("Fetching games data...");
      const gamesData = await getGames();
      // console.log("Games data received:", gamesData);
      if (Array.isArray(gamesData) && gamesData.length === 0) {
        console.warn("Games data is an empty array");
      }
      setGames(gamesData);
      setGamesLoading(false);
    } catch (error) {
      console.error("Error fetching games:", error);
      setGamesLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !issueArea ||
      !description ||
      !dateOfIssue ||
      (issueArea === "GAME" && !gameName) ||
      (!isAirdropVerification && !severity) ||
      (!isAirdropVerification && screenshots.length === 0) ||
      ((issueArea === "NODE REWARDS" || issueArea === "NODE HOSTING") &&
        (!subCategory || !claimingFrom || !walletType || !discord))
    ) {
      setShowFormErrors(true);
      return;
    }

    setIsSubmitting(true);

    let DOI = dateOfIssue ? new Date(dateOfIssue) : new Date();
    let CDT = new Date();

    // Compare only the date parts by resetting both to midnight UTC
    const doiDateOnly = DOI
      ? new Date(Date.UTC(DOI.getFullYear(), DOI.getMonth(), DOI.getDate()))
      : null;
    const cdtDateOnly = new Date(
      Date.UTC(CDT.getFullYear(), CDT.getMonth(), CDT.getDate()),
    );

    if (doiDateOnly && doiDateOnly > cdtDateOnly) {
      setActiveError("Date of Issue cannot be in the future.");
      setActiveErrorTitle("Error");
      setShowErrorModal(true);
      setIsSubmitting(false);
      return;
    }

    let reportData = {
      report_id: report ? report.report_id : reportId.rnd(),
      uid: uid,
      wallet: walletAddress,
      datetime_current: CDT,
      status: status,
      issue_area: issueArea,
      severity: isAirdropVerification ? severity || null : severity,
      date_time_issue: DOI,
      description: description,
      screenshots: isAirdropVerification ? [] : screenshots || [],
      admin_is_rewarded: report ? report.admin_is_rewarded : false,
      admin_points_awarded: report ? report.admin_points_awarded : 0,
      updated_at: report
        ? parseInt((new Date().getTime() / 1000).toFixed(0))
        : null,
      game_name: gameName || null,
      sub_category: subCategory || null,
      claiming_from: claimingFrom || null,
      wallet_type: walletType || null,
      telegram: telegram || null,
      email: email || null,
      discord: discord || null,
      round: round || null,
      buy_from: buyFrom || null,
      purchase_location: purchaseLocation || null,
    };

    try {
      if (!isAirdropVerification && screenshots.length > 0) {
        try {
          const screenshotUrls = await uploadReportScreenshots(
            reportData.report_id,
            screenshots,
          );
          reportData.screenshots = screenshotUrls;
        } catch (error) {
          setActiveError(error.message);
          setActiveErrorTitle("Error");
          setShowErrorModal(true);
          setIsSubmitting(false);
          return;
        }
      }

      let success = false;

      if (report) {
        console.log("Original Report Data: ", report);
        console.log("Updated Report Data: ", reportData);
        success = await updateUserReport(reportData.report_id, reportData);
      } else {
        const result = await addUserReport(reportData);
        success = !!result;
      }

      if (success) {
        onClose(true);
      } else {
        setActiveError("Failed to submit report. Please try again.");
        setActiveErrorTitle("Error");
        setShowErrorModal(true);
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Error submitting report:", error);
      setActiveError(
        error.message || "An error occurred while submitting the report",
      );
      setActiveErrorTitle("Error");
      setShowErrorModal(true);
      setIsSubmitting(false);
    }
  };

  const handleScreenshotChange = (e) => {
    if (e.target.files.length > 0) {
      let newFiles = [];
      for (let i = 0; i < e.target.files.length; i++) {
        newFiles.push(e.target.files[i]);
      }

      // Check if any file is larger than 5MB
      const maxFileSize = 5 * 1024 * 1024; // 5MB in bytes
      for (let i = 0; i < newFiles.length; i++) {
        if (newFiles[i].size > maxFileSize) {
          setActiveError(
            "One or more files exceed the maximum file size of 5MB.",
          );
          setActiveErrorTitle("Error");
          setShowErrorModal(true);
          return;
        }
      }

      // Check the total number of screenshots
      setScreenshots((prevScreenshots) => {
        const totalScreenshots = [...prevScreenshots, ...newFiles];

        // Check the total length of the screenshots array
        if (totalScreenshots.length > 5) {
          // Show an error message if the total length is more than 5
          setActiveError(
            "You can't upload more than 5 screenshots. Please delete some existing screenshots first.",
          );
          setActiveErrorTitle("Error");
          setShowErrorModal(true);

          // Clear the input but keep the existing screenshots
          e.target.value = null;
          return prevScreenshots;
        }

        return totalScreenshots;
      });
    }
  };
  const handleDateChange = (date) => {
    setDateOfIssue(date.target.value);
  };

  const handleRemoveScreenshot = (indexToRemove) => {
    setScreenshots((prev) =>
      prev.filter((_, index) => index !== indexToRemove),
    );
  };

  return (
    <Fragment>
      <Modal
        activeModal={isOpen}
        onClose={onClose}
        centered={true}
        title={report ? "Update Report" : "Submit Report"}
        bodyClass={"!py-4 !px-0"}
        themeClass="bg-metaxseed-blue !px-6 !py-3"
        className="max-w-xl bg-metaxseed-dark-gray report-modal"
        dismissable={false}
      >
        <div className="flex flex-col items-stretch justify-stretch flex-1">
          <div className=" px-6 flex flex-col items-stretch justify-stretch  overflow-y-auto max-h-[70vh]">
            {!report && (
              <Fragment>
                <div className="flex-1 flex flex-row items-stretch justify-stretch relative mb-2">
                  <input
                    type="text"
                    placeholder="UID"
                    className="font-normal gothamBook border text-xs rounded block w-full px-2.5 py-2 bg-zinc-700 border-zinc-600 placeholder-zinc-400 text-zinc-400 opacity-100 !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none"
                    disabled
                    value={uid}
                  />
                  <div className="text-xs min-w-24 absolute right-[1px] top-1/2 transform -translate-y-1/2 gothamMedium pl-4 pr-2 bg-zinc-700 text-zinc-400 custom-placeholder-report z-10 cursor-default">
                    <Icon icon="mdi:account" className="text-xs mr-2" />
                    <span>UID</span>
                  </div>
                </div>
                <div className="flex-1 flex flex-row items-stretch justify-stretch relative mb-2">
                  <input
                    type="text"
                    placeholder="Wallet Address"
                    className="font-normal gothamBook border text-xs rounded block w-full px-2.5 py-2 bg-zinc-700 border-zinc-600 placeholder-zinc-400 text-zinc-400 opacity-100 !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none"
                    disabled
                    value={walletAddress}
                  />
                  <div className="text-xs min-w-24 absolute right-[1px] top-1/2 transform -translate-y-1/2 gothamMedium pl-4 pr-2 bg-zinc-700 text-zinc-400 custom-placeholder-report z-10 cursor-default">
                    <Icon icon="mdi:wallet" className="text-xs mr-2" />
                    <span>Wallet</span>
                  </div>
                </div>
                <div className="flex-1 flex flex-row items-stretch justify-stretch relative mb-2">
                  <input
                    type="text"
                    placeholder="Current Date/Time"
                    className="font-normal gothamBook border text-xs rounded block w-full px-2.5 py-2 bg-zinc-700 border-zinc-600 placeholder-zinc-400 text-zinc-400 opacity-100 !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none"
                    disabled
                    value={currentDateTime}
                  />
                  <div className="text-xs min-w-24 absolute right-[1px] top-1/2 transform -translate-y-1/2 gothamMedium pl-4 pr-2 bg-zinc-700 text-zinc-400 custom-placeholder-report z-10 cursor-default">
                    <Icon
                      icon="mdi:clock-time-four-outline"
                      className="text-xs mr-2"
                    />
                    <span>Time</span>
                  </div>
                </div>

                <div className="flex-1 flex flex-row items-stretch justify-stretch relative">
                  <select
                    className="font-normal gothamBook border text-xs rounded block w-full px-2.5 py-2 bg-zinc-700 border-zinc-600 placeholder-zinc-400 text-zinc-400 appearance-none opacity-100 !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none"
                    disabled
                    value={status}
                  >
                    <option value="OPEN">OPEN</option>
                    <option value="CLOSED">CLOSED</option>
                    <option value="PENDING">PENDING</option>
                    <option value="REJECTED">REJECTED</option>
                  </select>
                  <div className="text-xs min-w-24 absolute right-[1px] top-1/2 transform -translate-y-1/2 gothamMedium pl-4 pr-2 bg-zinc-700 text-zinc-400 custom-placeholder-report z-10 cursor-default">
                    <Icon icon="el:question-sign" className="text-xs mr-2" />
                    <span>Status</span>
                  </div>
                </div>

                <div className="flex-1 flex flex-row items-stretch justify-stretch relative mb-4 mt-6">
                  <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-zinc-700"></div>
                  <div className="h-[1px] flex-1 bg-gradient-to-r from-zinc-700 to-transparent"></div>
                </div>
              </Fragment>
            )}

            {report && (
              <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
                <label
                  className="block text-sm gothamBook mb-1"
                  htmlFor="status"
                >
                  Status:
                </label>
                <select
                  className={
                    "font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700  placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none" +
                    (showFormErrors && !status
                      ? " border-red-500"
                      : " border-zinc-600")
                  }
                  value={status}
                  required
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option disabled value="" style={{ color: "#a0aec0" }}>
                    Choose one
                  </option>
                  {status && <option value={status}>{status}</option>}
                  <option value="USER_REOPENED">USER_REOPENED</option>
                  <option value="USER_SOLVED">USER_SOLVED</option>
                </select>
                {showFormErrors && !status && (
                  <small className="text-xs text-red-500 gothamBook">
                    <span className="text-red-500">*</span> Required
                  </small>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
                <label
                  className="block text-sm gothamBook mb-1"
                  htmlFor="issueArea"
                >
                  Issue Area:
                </label>
                <select
                  className={
                    "font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700  placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none" +
                    (showFormErrors && !issueArea
                      ? " border-red-500"
                      : " border-zinc-600")
                  }
                  value={issueArea}
                  disabled={report ? true : false}
                  required
                  onChange={(e) => setIssueArea(e.target.value)}
                >
                  <option disabled value="" style={{ color: "#a0aec0" }}>
                    Choose one
                  </option>
                  <option value="AIRDROP VERIFICATION">
                    AIRDROP VERIFICATION
                  </option>
                  <option value="LOYALTY">LOYALTY</option>
                  <option value="SEASONS">SEASONS</option>
                  <option value="WEBSITE">WEBSITE</option>
                  <option value="WALLET">WALLET</option>
                  <option value="PERFORMANCE">PERFORMANCE</option>
                  <option value="DATA">DATA</option>
                  <option value="GAME">GAME</option>
                  <option value="MXS PLAY LAUNCHER (PC)">
                    MXS PLAY LAUNCHER (PC)
                  </option>
                  <option value="MISSIONS">MISSIONS</option>
                  <option value="NODES">NODES</option>
                  <option value="NODE REWARDS">NODE REWARDS</option>
                  <option value="NODE HOSTING">NODE HOSTING</option>
                  <option value="NFT">NFT</option>
                  <option value="TOKEN STAKE">TOKEN STAKE</option>
                  <option value="TOKEN VESTING">TOKEN VESTING</option>
                  <option value="OTHER">OTHER</option>
                </select>
                {showFormErrors && !issueArea && (
                  <small className="text-xs text-red-500 gothamBook">
                    <span className="text-red-500">*</span> Required
                  </small>
                )}
              </div>
              {!isAirdropVerification && (
                <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
                  <label
                    className="block text-sm gothamBook mb-1"
                    htmlFor="severity"
                  >
                    Severity:
                  </label>
                  <select
                    className={
                      "font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700 placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none" +
                      (showFormErrors && !severity
                        ? " border-red-500"
                        : " border-zinc-600")
                    }
                    value={severity}
                    disabled={report ? true : false}
                    required
                    onChange={(e) => setSeverity(e.target.value)}
                  >
                    <option disabled value="" style={{ color: "#a0aec0" }}>
                      Choose one
                    </option>
                    <option value="LOW">LOW</option>
                    <option value="MEDIUM">MEDIUM</option>
                    <option value="HIGH">HIGH</option>
                    <option value="CRITICAL">CRITICAL</option>
                  </select>
                  {showFormErrors && !severity && (
                    <small className="text-xs text-red-500 gothamBook">
                      <span className="text-red-500">*</span> Required
                    </small>
                  )}
                </div>
              )}
              {(issueArea === "NODE REWARDS" ||
                issueArea === "NODE HOSTING") && (
                <Fragment>
                  <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
                    <label
                      className="block text-sm gothamBook mb-1"
                      htmlFor="subCategory"
                    >
                      Category: <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="subCategory"
                      name="subCategory"
                      className={
                        "font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700 placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none" +
                        (showFormErrors && !subCategory
                          ? " border-red-500"
                          : " border-zinc-600")
                      }
                      value={subCategory}
                      onChange={(e) => setSubCategory(e.target.value)}
                    >
                      <option value="" disabled>
                        Select a category
                      </option>
                      <option value="CAN NOT CLAIM MY REWARDS">
                        Can not claim my rewards
                      </option>
                      <option value="MY REWARDS IS INCORRECT">
                        My rewards is incorrect
                      </option>
                      <option value="NEXT REWARDS IS INCORRECT">
                        Next rewards is incorrect
                      </option>
                      <option value="CLAIMED REWARDS IS INCORRECT">
                        Claimed rewards is incorrect
                      </option>
                      <option value="UNABLE TO CLAIM">Unable to claim</option>
                      <option value="NODE LICENSES NOT REFLECTING">
                        Node licenses not reflecting
                      </option>
                      <option value="WALLET XSEED BALANCE INCORRECT">
                        Wallet XSEED balance incorrect
                      </option>
                      <option value="REWARD GENERATIONS INACTIVE">
                        Reward generations inactive
                      </option>
                      <option value="LOYALTY OR ENGAGEMENT SCORE INCORRECT">
                        Loyalty or engagement score incorrect
                      </option>
                      <option value="HOSTING FEE PAYMENT NOT REFLECTED">
                        Hosting fee payment not reflected
                      </option>
                      <option value="CAN NOT PAY HOSTING FEE">
                        Can not pay hosting fee
                      </option>
                      <option value="MY CLAIM HISTORY NOT SHOWING">
                        My claim history not showing
                      </option>
                      <option value="KYC COMPLETED BUT CANNOT CLAIM">
                        KYC completed but cannot claim
                      </option>
                    </select>
                    {showFormErrors && !subCategory && (
                      <small className="text-xs text-red-500 gothamBook">
                        <span className="text-red-500">*</span> Required
                      </small>
                    )}
                  </div>

                  <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
                    <label
                      className="block text-sm gothamBook mb-1"
                      htmlFor="claimingFrom"
                    >
                      Claiming From: <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="claimingFrom"
                      name="claimingFrom"
                      className={
                        "font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700 placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none" +
                        (showFormErrors && !claimingFrom
                          ? " border-red-500"
                          : " border-zinc-600")
                      }
                      value={claimingFrom}
                      onChange={(e) => setClaimingFrom(e.target.value)}
                    >
                      <option value="" disabled>
                        Select Device
                      </option>
                      <option value="MOBILE">Mobile</option>
                      <option value="PC">Pc</option>
                    </select>
                    {showFormErrors && !claimingFrom && (
                      <small className="text-xs text-red-500 gothamBook">
                        <span className="text-red-500">*</span> Required
                      </small>
                    )}
                  </div>

                  <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
                    <label
                      className="block text-sm gothamBook mb-1"
                      htmlFor="walletType"
                    >
                      Wallet Type: <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="walletType"
                      name="walletType"
                      className={
                        "font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700 placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none" +
                        (showFormErrors && !walletType
                          ? " border-red-500"
                          : " border-zinc-600")
                      }
                      value={walletType}
                      onChange={(e) => setWalletType(e.target.value)}
                    >
                      <option value="" disabled>
                        Select wallet type
                      </option>
                      <option value="METAMASK">Metamask</option>
                      <option value="RABBY_WALLET">Rabby Wallet</option>
                      <option value="LEDGER">Ledger</option>
                      <option value="TANGEM">Tangem</option>
                      <option value="TRUST_WALLET">Trust Wallet</option>
                      <option value="OKX_WALLET">OKX Wallet</option>
                      <option value="COINBASE">Coinbase</option>
                      <option value="OTHER">Other</option>
                    </select>
                    {showFormErrors && !walletType && (
                      <small className="text-xs text-red-500 gothamBook">
                        <span className="text-red-500">*</span> Required
                      </small>
                    )}
                  </div>

                  <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
                    <label
                      className="block text-sm gothamBook mb-1"
                      htmlFor="telegram"
                    >
                      Your Telegram:
                    </label>
                    <input
                      type="text"
                      id="telegram"
                      name="telegram"
                      className="font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700 border-zinc-600 placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none"
                      value={telegram}
                      onChange={(e) => setTelegram(e.target.value)}
                    />
                  </div>

                  <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
                    <label
                      className="block text-sm gothamBook mb-1"
                      htmlFor="discord"
                    >
                      Your Discord: <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="discord"
                      name="discord"
                      className={
                        "font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700 placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none" +
                        (showFormErrors && !discord
                          ? " border-red-500"
                          : " border-zinc-600")
                      }
                      value={discord}
                      onChange={(e) => setDiscord(e.target.value)}
                    />
                    {showFormErrors && !discord && (
                      <small className="text-xs text-red-500 gothamBook">
                        <span className="text-red-500">*</span> Required
                      </small>
                    )}
                  </div>

                  <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
                    <label
                      className="block text-sm gothamBook mb-1"
                      htmlFor="email"
                    >
                      Your Email:
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700 border-zinc-600 placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </Fragment>
              )}
              {(issueArea === "TOKEN AIRDROP" ||
                issueArea === "TOKEN VESTING") && (
                <Fragment>
                  <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
                    <label className="block text-sm gothamBook mb-1">
                      Round: <span style={{ color: "red" }}>*</span>
                    </label>
                    <select
                      className="font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700 placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none"
                      value={round}
                      required
                      onChange={(e) => setRound(e.target.value)}
                    >
                      <option disabled value="" style={{ color: "#a0aec0" }}>
                        Choose one
                      </option>
                      <option value="SEED">SEED</option>
                      <option value="STRATEGIC">STRATEGIC</option>
                      <option value="PRIVATE">PRIVATE</option>
                      <option value="PUBLIC">PUBLIC</option>
                    </select>
                  </div>

                  <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
                    <label className="block text-sm gothamBook mb-1">
                      Buy From: <span style={{ color: "red" }}>*</span>
                    </label>
                    <select
                      className="font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700 placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none"
                      value={buyFrom}
                      required
                      onChange={(e) => setBuyFrom(e.target.value)}
                    >
                      <option disabled value="" style={{ color: "#a0aec0" }}>
                        Choose one
                      </option>
                      <option value="VC">VC</option>
                      <option value="LAUNCHPAD">LAUNCHPAD</option>
                      <option value="DIRECT">DIRECT</option>
                    </select>
                  </div>

                  <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
                    <label className="block text-sm gothamBook mb-1">
                      Where did you buy the token?{" "}
                      <span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="text"
                      className="font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700 placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none"
                      value={purchaseLocation}
                      required
                      onChange={(e) => setPurchaseLocation(e.target.value)}
                    />
                  </div>
                </Fragment>
              )}

              {issueArea === "AIRDROP VERIFICATION" && (
                <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
                  <label
                    className="block text-sm gothamBook mb-1"
                    htmlFor="dateOfIssue"
                  >
                    Date of Issue:
                  </label>
                  <input
                    type="date"
                    className={
                      "font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700 placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none" +
                      (showFormErrors && !dateOfIssue
                        ? " border-red-500"
                        : " border-zinc-600")
                    }
                    required
                    disabled={report ? true : false}
                    value={
                      dateOfIssue
                        ? new Date(dateOfIssue).toISOString().split("T")[0]
                        : ""
                    }
                    onChange={handleDateChange}
                  />
                  {showFormErrors && !dateOfIssue && (
                    <small className="text-xs text-red-500 gothamBook">
                      <span className="text-red-500">*</span> Required
                    </small>
                  )}
                </div>
              )}

              {(issueArea === "GAME" ||
                issueArea === "MXS PLAY LAUNCHER (PC)") &&
                !gamesLoading && (
                  <>
                    {issueArea === "GAME" && (
                      <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
                        <label
                          className="block text-sm gothamBook mb-1"
                          htmlFor="game_name"
                        >
                          Game name:
                        </label>
                        <select
                          className={
                            "font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700  placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none" +
                            (showFormErrors && issueArea === "GAME" && !gameName
                              ? " border-red-500"
                              : " border-zinc-600")
                          }
                          value={gameName}
                          required
                          disabled={report ? true : false}
                          onChange={(e) => setGameName(e.target.value)}
                        >
                          <option
                            disabled
                            value=""
                            style={{ color: "#a0aec0" }}
                          >
                            Choose one
                          </option>
                          {games.length > 0 ? (
                            games.map((game, index) => (
                              <option value={game.game_value} key={index}>
                                {game.game_name
                                  ? game.game_name.toUpperCase()
                                  : "Unknown Game"}
                              </option>
                            ))
                          ) : (
                            <option disabled>No games available</option>
                          )}
                        </select>
                        {showFormErrors &&
                          issueArea === "GAME" &&
                          !gameName && (
                            <small className="text-xs text-red-500 gothamBook">
                              <span className="text-red-500">*</span> Required
                            </small>
                          )}
                      </div>
                    )}

                    {/* Game Version field removed as per requirements */}
                  </>
                )}

              {!isAirdropVerification && issueArea !== "AIRDROP VERIFICATION" && (
                <>
                  <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
                    <label
                      className="block text-sm gothamBook mb-1"
                      htmlFor="dateOfIssue"
                    >
                      Date of Issue:
                    </label>
                    <input
                      type="date"
                      className={
                        "font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700 placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none" +
                        (showFormErrors && !dateOfIssue
                          ? " border-red-500"
                          : " border-zinc-600")
                      }
                      required
                      disabled={report ? true : false}
                      value={
                        dateOfIssue
                          ? new Date(dateOfIssue).toISOString().split("T")[0]
                          : ""
                      }
                      onChange={handleDateChange}
                    />
                    {showFormErrors && !dateOfIssue && (
                      <small className="text-xs text-red-500 gothamBook">
                        <span className="text-red-500">*</span> Required
                      </small>
                    )}
                  </div>

                  <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
                    <label
                      className="block text-sm gothamBook mb-1"
                      htmlFor="screenshots"
                    >
                      Upload Screenshots:
                    </label>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className={
                        "font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700 placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none"
                      }
                      required
                      onChange={handleScreenshotChange}
                    />
                    {showFormErrors && screenshots.length === 0 && (
                      <small className="text-xs text-red-500 gothamBook">
                        <span className="text-red-500">*</span> Required
                      </small>
                    )}

                    {previewUrls && previewUrls.length > 0 && (
                      <div className="mt-2 flex flex-row flex-wrap gap-2">
                        {previewUrls.map((url, index) => (
                          <div
                            key={index}
                            className="relative w-16 h-16 rounded overflow-hidden border border-zinc-600 bg-zinc-800 group"
                            title={`Screenshot ${index + 1}`}
                          >
                            <img
                              src={url}
                              alt={`screenshot-${index + 1}`}
                              className="w-full h-full object-cover cursor-pointer"
                              onClick={() => setActivePreviewIndex(index)}
                            />
                            <button
                              type="button"
                              aria-label="Remove screenshot"
                              className="absolute top-0.5 right-0.5 p-0.5 rounded bg-black/60 hover:bg-black/80 text-white hidden group-hover:block"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveScreenshot(index);
                              }}
                            >
                              <Icon icon="mdi:close" className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
              <label
                className="block text-sm gothamBook mb-1"
                htmlFor="description"
              >
                Description:
              </label>
              <textarea
                className={
                  "font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700  placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none" +
                  (showFormErrors && !description
                    ? " border-red-500"
                    : " border-zinc-600")
                }
                required
                disabled={report ? true : false}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              ></textarea>
              {showFormErrors && !description && (
                <small className="text-xs text-red-500 gothamBook">
                  <span className="text-red-500">*</span> Required
                </small>
              )}
            </div>
          </div>

          <div className=" px-6 flex flex-col items-stretch justify-stretch">
            <div className="flex-1 flex flex-row items-stretch justify-stretch relative mt-4">
              {isSubmitting && (
                <Button
                  className={
                    "font-normal w-full btn-secondary !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none z-10 "
                  }
                  isLoading
                />
              )}
              {!isSubmitting && (
                <Button
                  onClick={handleSubmit}
                  text="Submit"
                  className={
                    "font-normal w-full btn-primary !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none z-10 "
                  }
                />
              )}
            </div>
          </div>
        </div>
      </Modal>

      {activePreviewIndex !== null &&
        previewUrls[activePreviewIndex] &&
        typeof document !== "undefined" &&
        createPortal(
          <div className="fixed inset-0 z-[2147483647] bg-black/80 flex items-center justify-center">
            <div
              className="relative max-w-5xl w-[90%] max-h-[90vh] bg-zinc-900 rounded shadow-lg p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={previewUrls[activePreviewIndex]}
                alt="screenshot-large"
                className="w-full h-full max-h-[75vh] object-contain rounded"
              />
              <button
                type="button"
                className="absolute top-2 right-2 px-2 py-1 bg-zinc-800 hover:bg-zinc-700 text-white rounded"
                onClick={() => setActivePreviewIndex(null)}
              >
                <Icon icon="mdi:close" className="w-5 h-5" />
              </button>
              <button
                type="button"
                className="absolute left-2 top-1/2 -translate-y-1/2 px-3 py-2 bg-zinc-800/70 hover:bg-zinc-700 text-white rounded"
                onClick={() =>
                  setActivePreviewIndex((prev) => Math.max(0, (prev ?? 0) - 1))
                }
                disabled={activePreviewIndex === 0}
              >
                <Icon icon="mdi:chevron-left" className="w-6 h-6" />
              </button>
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-2 bg-zinc-800/70 hover:bg-zinc-700 text-white rounded"
                onClick={() =>
                  setActivePreviewIndex((prev) =>
                    Math.min(previewUrls.length - 1, (prev ?? 0) + 1),
                  )
                }
                disabled={activePreviewIndex === previewUrls.length - 1}
              >
                <Icon icon="mdi:chevron-right" className="w-6 h-6" />
              </button>
              <div className="text-center text-xs text-zinc-300 mt-2">
                {(activePreviewIndex ?? 0) + 1} / {previewUrls.length}
              </div>
            </div>
          </div>,
          document.body,
        )}

      {showErrorModal && (
        <MessageModal
          title={activeErrorTitle || "Error"}
          status={false}
          centered
          activeModal={showErrorModal}
          className="max-w-xl errorModalShadow"
          onClose={(event) => {
            if (event) {
              setShowErrorModal(false);
              setTimeout(() => {
                setActiveError(null);
                setActiveErrorTitle(null);
              }, 300);
            }
          }}
        >
          <div className="font-medium py-2">
            {activeError || "An error occurred. Please try again later."}
          </div>
        </MessageModal>
      )}
    </Fragment>
  );
}

export default ReportModal;
